var React = require('react');
var { connect } = require('react-redux');
var { Chart } = require('chart.js');
var helpers = require('chart.js/helpers');
var dayjs = require('../../utils/dayjsConfig');
var { upper } = require('helpers');
var { unitDisplayName } = require('unitsManager');
var { withTranslation } = require('react-i18next');
var RightArrow = require('images/icons/right.svg');
var LeftArrow = require('images/icons/left.svg');
var { EventPopUp } = require('./EventPopUp.jsx');
var {
  resolveActiveModeSuggestedMin,
  resolveForcedZeroFloor,
  shouldKeepZeroBaselineTick,
} = require('./sensorGraphYAxisHelpers');
var fieldManager = require('fieldManager');
var distributionActions = require('@actions/distributionActions');
require('./ConfigureChartjs');

var { pressureAlertLevel } = require('@utils/pressureAlert');
var { formatIrrigationAmount } = require('@utils/irrigationAmount');
var PressureAlertIcon = require('images/icons/graph/pressure_alert.svg');

let timer;

const TIME_UNIT_ORDER = ['minute', 'hour', 'day', 'week', 'month', 'year'];

// Event popup chrome. The redesign (Figma 12009:11615) swaps the legacy 5px
// card for an 8px one with a hairline border and its own padding; the legacy
// style stays for every graph that has not opted in.
const NEW_POPUP_MENU_CLASSES = 'dropdown-menu mt-0 ';
const LEGACY_POPUP_MENU_CLASSES = 'dropdown-menu pr-2 pl-2 mt-0 ';
const NEW_POPUP_MENU_STYLE =
  'border-radius: 8px;border: 1px solid var(--color-grey-91);padding: 8px;' +
  'box-shadow: 0px 4px 10px rgba(145, 157, 180, 0.19)';
const LEGACY_POPUP_MENU_STYLE =
  'border-radius: 5px;box-shadow: 0px 4px 10px rgba(145, 157, 180, 0.19)';

// Pressure-alert badge in the tray (Figma 12009:11613): 12x12, sitting 2px
// above the irrigation bar and nudged 1.5px in from its leading edge.
const PRESSURE_ALERT_BADGE_SIZE = 12;
const PRESSURE_ALERT_BADGE_GAP = 2;
const PRESSURE_ALERT_BADGE_INSET = 1.5;

// Caret height — must match ARROW_HEIGHT in graphEventCard.js, which draws it.
const POPUP_ARROW_HEIGHT = 6;

// Popup opened from the pressure-alert badge: it lifts ABOVE the badge with a
// caret pointing down at it, because a popup dropped below would cover the very
// 12px icon the user is pointing at. Everything else keeps the normal downward
// dropdown (NEW_POPUP_MENU_STYLE).
//
// POSITION: FIXED, not absolute. The graph card
// (.field-details-wp__frame) is `overflow: hidden` to clip its own rounded
// corners, which cropped a tall grouped popup to the chart. A fixed element is
// not clipped by an ancestor's overflow, and escapes any ancestor stacking
// context, so the card can extend over the rest of the dashboard. This works
// only while no ancestor of the tray has transform/filter/will-change — any of
// those would make that ancestor the containing block and the clipping would
// return. The popup stays a DOM child of the bar so the CSS hover mechanism
// (.dropdown-hover:hover > .dropdown-menu) is untouched; :hover follows the DOM
// tree, not layout.
//
// `left`/`top` are filled in per hover from the badge's measured viewport rect;
// translate(-50%, -100%) centres the card on the badge and lifts it clear. The
// caret is centred on the card in turn, so it lands on the badge without either
// knowing the other's geometry. `transition: none` cancels the tray's 0.3s
// dropdown slide (main.css `.dropdown-hover > .dropdown-menu`) — the card must
// appear pinned to the icon, not glide into place.
const BADGE_POPUP_Z_INDEX = 1070;
// Gap kept between the top of the card and the top of the viewport.
const BADGE_POPUP_VIEWPORT_MARGIN = 12;
// The popup's own padding (8px x2) plus its 1px borders, which sit outside the
// scrollable card and so must come off the height available to it.
const BADGE_POPUP_CHROME = 18;
// Below this a scrolling card is more annoying than useful; let it overhang.
const BADGE_POPUP_MIN_HEIGHT = 120;

const BADGE_POPUP_MENU_STYLE =
  NEW_POPUP_MENU_STYLE +
  ';position: fixed;margin: 0;transform: translate(-50%, -100%);' +
  'transition: none;z-index: ' +
  BADGE_POPUP_Z_INDEX;

// Pins the popup above the badge, in viewport coordinates. `top` is the card's
// bottom edge (translate(-100%) lifts it by its own height), set so the caret's
// tip lands exactly on the badge's top edge.
function applyBadgePopupPosition(menu, badge) {
  const rect = badge.getBoundingClientRect();
  menu.style.cssText =
    BADGE_POPUP_MENU_STYLE +
    ';left: ' +
    (rect.left + rect.width / 2) +
    'px;top: ' +
    (rect.top - POPUP_ARROW_HEIGHT) +
    'px';
}

// Caps the card to the room actually above the badge and lets it scroll, so a
// day with many alerts cannot run off the top of the viewport.
//
// The cap goes on the inner card, NOT the popup: the caret hangs outside the
// popup's box (`bottom: -7px`), and a scroll container on the popup would clip
// it away. `overscroll-behavior: contain` stops a wheel scroll inside the card
// from chaining to the page and dragging the badge out from under the pointer.
function capBadgePopupHeight(menu, badge) {
  const card = menu.firstElementChild;
  if (!card) {
    return;
  }
  const available =
    badge.getBoundingClientRect().top -
    POPUP_ARROW_HEIGHT -
    BADGE_POPUP_VIEWPORT_MARGIN -
    BADGE_POPUP_CHROME;
  card.style.maxHeight = Math.max(available, BADGE_POPUP_MIN_HEIGHT) + 'px';
  card.style.overflowY = 'auto';
  card.style.overscrollBehavior = 'contain';
}

// Places the popup for a hover mode: 'above' is the badge-anchored card, 'below'
// the ordinary dropdown. Only the redesigned popup has two modes.
function applyPopupMode(menu, mode, rightMenuClass, badge) {
  menu.className =
    NEW_POPUP_MENU_CLASSES + (mode === 'above' ? '' : rightMenuClass);
  if (mode !== 'above' || !badge) {
    menu.style.cssText = NEW_POPUP_MENU_STYLE;
    return;
  }
  applyBadgePopupPosition(menu, badge);
}

// Marks an irrigation bar whose pressure breached the sensor's boundaries. The
// badge is a child of the bar so hovering it opens the very same event popup
// (.dropdown-hover:hover > .dropdown-menu matches on descendants too), which is
// what the design asks for.
function buildPressureAlertBadge() {
  var badge = document.createElement('img');
  badge.src = PressureAlertIcon;
  badge.className = 'position-absolute';
  badge.style.cssText =
    'width:' +
    PRESSURE_ALERT_BADGE_SIZE +
    'px;height:' +
    PRESSURE_ALERT_BADGE_SIZE +
    'px;left:' +
    PRESSURE_ALERT_BADGE_INSET +
    'px;top:' +
    -(PRESSURE_ALERT_BADGE_SIZE + PRESSURE_ALERT_BADGE_GAP) +
    'px';
  return badge;
}

// Minimum visible width (px) for an event bar when it fits inside the plot.
const MIN_EVENT_BAR_WIDTH = 3;
// Clamp an event bar's horizontal geometry so it can never leave the plot area,
// regardless of the selected time range (1D/1W/2W/1M/6M/1Y) or event duration.
// Returns { left, width } in canvas-relative pixels, or null when the bar would
// fall entirely at/after the right edge (nothing to draw).
function clampEventBarGeometry(rawLength, eventXPos, chartArea) {
  var left = Math.min(Math.max(eventXPos, chartArea.left), chartArea.right);
  var maxWidth = chartArea.right - left;
  if (maxWidth <= 0) {
    return null;
  }
  var width = Math.min(Math.max(MIN_EVENT_BAR_WIDTH, rawLength), maxWidth);
  return { left: left, width: width };
}

// Decide tooltip vertical alignment so caret stays inside chart area
function computeTooltipYAlign(context) {
  const chartArea = context.chart.chartArea;
  const chartHeight = chartArea.bottom - chartArea.top;
  const pointerHeight = context?.tooltip?.caretY;
  const tooltipHeight = context?.tooltip?.height;

  if (
    typeof pointerHeight === 'number' &&
    typeof tooltipHeight === 'number' &&
    pointerHeight >= chartHeight - tooltipHeight
  ) {
    return 'bottom';
  }
  return 'center';
}

class SensorDataStream {
  constructor(
    id,
    samples,
    color,
    upperRange,
    lowerRange,
    displayName,
    transmitterId,
  ) {
    this.id = id;
    this.samples = samples;
    this.color = color;
    this.upperRange = upperRange;
    this.lowerRange = lowerRange;
    this.displayName = displayName;
    this.transmitterId = transmitterId;

    this.curDayOverLay = null;
  }
}

class SensorGraph extends React.Component {
  constructor(props) {
    super(props);
    this.updateChart = this.updateChart.bind(this);
    this.updateOverlaySize = this.updateOverlaySize.bind(this);
    this.manipulateDailyData = this.manipulateDailyData.bind(this);
    this._invalidateOverlayCache = this._invalidateOverlayCache.bind(this);
    this._handleChartContainerResize =
      this._handleChartContainerResize.bind(this);

    this._chartWrapperRef = React.createRef();
    this._resizeObserver = null;
    this._resizeRAF = null;
    this._lastObservedWidth = null;
    this._lastObservedHeight = null;
    this._isApplyingChartResize = false;
    this._pendingChartResize = null;

    this.current_chart = null;
    this.dailyData = null;
    this.timezone = null;
    this.temperatureStatistics = {};
    this.etcStatistics = {};

    this.rainSamples = {};
    this.sensorData = {};
    this.upper_range = 0;
    this.lower_range = 0;

    this.DatesArray = [];

    // Track current field to detect field changes
    this.currentFieldId = null;

    // Cache for beforeDraw plugin to skip redundant work on tooltip hovers
    this._cachedXMin = null;
    this._cachedXMax = null;
    this._cachedChartWidth = null;
    this._cachedChartHeight = null;
    this._cachedDailyDataRef = null;
    this._cachedTickLabels = null;
  }

  // Resolve CSS variables (e.g., 'var(--color-...)') to actual color values for canvas
  resolveColor(color) {
    if (typeof color === 'string' && color.startsWith('var(')) {
      try {
        const token = color.slice(4, -1).trim();
        const resolved = getComputedStyle(document.documentElement)
          .getPropertyValue(token)
          .trim();
        return resolved || color;
      } catch (_) {
        return color;
      }
    }
    return color;
  }

  componentDidMount() {
    this.props.onRef(this);

    window.addEventListener('resize', this.updateOverlaySize);

    if (
      typeof ResizeObserver !== 'undefined' &&
      this._chartWrapperRef.current
    ) {
      this._resizeObserver = new ResizeObserver(
        this._handleChartContainerResize,
      );
      this._resizeObserver.observe(this._chartWrapperRef.current);
    }

    var canvas = document.getElementById('chartJSContainer');
    if (canvas) {
      this.setupChart();
    }
  }

  _invalidateOverlayCache() {
    this._cachedXMin = null;
    this._cachedXMax = null;
    this._cachedChartWidth = null;
    this._cachedChartHeight = null;
    this._cachedDailyDataRef = null;
  }

  _handleChartContainerResize(entries) {
    const entry = entries && entries[0];
    if (!entry) {
      return;
    }

    const width = Math.round(entry.contentRect.width);
    const height = Math.round(entry.contentRect.height);

    if (
      width === this._lastObservedWidth &&
      height === this._lastObservedHeight
    ) {
      return;
    }

    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF);
    }

    // Defer past the current ResizeObserver delivery so chart.resize() and
    // overlay updates cannot re-enter the observer in the same frame.
    this._resizeRAF = requestAnimationFrame(() => {
      this._resizeRAF = requestAnimationFrame(() => {
        this._resizeRAF = null;
        this._applyChartContainerResize(width, height);
      });
    });
  }

  _applyChartContainerResize(observedWidth, observedHeight) {
    if (this._isApplyingChartResize) {
      this._pendingChartResize = {
        width: observedWidth,
        height: observedHeight,
      };
      return;
    }
    this._isApplyingChartResize = true;

    try {
      this._invalidateOverlayCache();
      if (this.current_chart) {
        this.current_chart.resize();
        // resize() alone may not repaint; update() runs beforeDraw so labels
        // and #additionals rebuild after Advanced/Close layout changes.
        this.current_chart.update('none');
      }

      var overlay = document.getElementById('overlay');
      if (overlay && this.current_chart && this.current_chart.chartArea) {
        overlay.height =
          this.current_chart.chartArea.bottom -
          this.current_chart.chartArea.top +
          50;
        overlay.width =
          this.current_chart.chartArea.right -
          this.current_chart.chartArea.left +
          50;
        overlay.color = '#919DB4';
      }

      const el = this._chartWrapperRef.current;
      if (el) {
        this._lastObservedWidth =
          observedWidth != null ? observedWidth : Math.round(el.clientWidth);
        this._lastObservedHeight =
          observedHeight != null ? observedHeight : Math.round(el.clientHeight);
      }
    } finally {
      this._isApplyingChartResize = false;
      const pending = this._pendingChartResize;
      if (pending) {
        this._pendingChartResize = null;
        requestAnimationFrame(() => {
          this._applyChartContainerResize(pending.width, pending.height);
        });
      }
    }
  }

  // Find the earliest and latest dates from all sensor data for archive mode
  findDataDateRange() {
    let earliestDate = null;
    let latestDate = null;

    // Check if sensor data exists
    if (!this.sensorData || Object.keys(this.sensorData).length === 0) {
      return { earliestDate, latestDate };
    }

    for (const sensorKey in this.sensorData) {
      const sensorDataStream = this.sensorData[sensorKey];
      if (
        sensorDataStream &&
        sensorDataStream.samples &&
        sensorDataStream.samples.length > 0
      ) {
        const firstSample = sensorDataStream.samples[0];
        const lastSample =
          sensorDataStream.samples[sensorDataStream.samples.length - 1];

        if (firstSample && firstSample.date) {
          if (!earliestDate || dayjs(firstSample.date).isBefore(earliestDate)) {
            earliestDate = dayjs(firstSample.date);
          }
        }

        if (lastSample && lastSample.date) {
          if (!latestDate || dayjs(lastSample.date).isAfter(latestDate)) {
            latestDate = dayjs(lastSample.date);
          }
        }
      }
    }

    // Add padding for better visualization only if we have valid dates
    if (earliestDate && earliestDate.isValid()) {
      earliestDate = earliestDate.subtract(1, 'day');
    } else {
      earliestDate = null;
    }

    if (latestDate && latestDate.isValid()) {
      latestDate = latestDate.add(1, 'day');
    } else {
      latestDate = null;
    }

    return { earliestDate, latestDate };
  }

  // Find the latest date from all sensor data for archive mode (for backward compatibility)
  findLatestDataDate() {
    const { latestDate } = this.findDataDateRange();
    return latestDate;
  }

  // Calculate appropriate y-axis bounds based on actual data
  updateYAxisBounds() {
    if (!this.sensorData || Object.keys(this.sensorData).length === 0) {
      return;
    }

    let minValue = Number.POSITIVE_INFINITY;
    let maxValue = Number.NEGATIVE_INFINITY;

    // Find min/max values across all sensor data
    for (const sensorKey in this.sensorData) {
      const sensorDataStream = this.sensorData[sensorKey];
      if (
        sensorDataStream &&
        sensorDataStream.samples &&
        sensorDataStream.samples.length > 0
      ) {
        for (const sample of sensorDataStream.samples) {
          if (
            sample &&
            typeof sample.value === 'number' &&
            !isNaN(sample.value)
          ) {
            if (sample.value < minValue) minValue = sample.value;
            if (sample.value > maxValue) maxValue = sample.value;
          }
        }
      }
    }

    // If we found valid data, set appropriate bounds with padding
    if (
      minValue !== Number.POSITIVE_INFINITY &&
      maxValue !== Number.NEGATIVE_INFINITY
    ) {
      const range = maxValue - minValue;
      const padding = Math.max(range * 0.1, 1); // 10% padding or minimum 1 unit

      this.current_chart.options.scales.y.min = minValue - padding;
      this.current_chart.options.scales.y.max = maxValue + padding;

      // Remove suggested bounds to use our calculated bounds
      delete this.current_chart.options.scales.y.suggestedMin;
      delete this.current_chart.options.scales.y.suggestedMax;

      // For non-negative graphs, clamp the padded floor back up to 0 (the
      // padding above makes minValue - padding negative when minValue === 0).
      this.applyForcedZeroYAxisFloorIfNeeded();
    }
  }

  // Reset y-axis to default behavior
  resetYAxisBounds() {
    // Remove any fixed bounds
    delete this.current_chart.options.scales.y.min;
    delete this.current_chart.options.scales.y.max;

    // Restore default suggested bounds
    this.current_chart.options.scales.y.suggestedMin = -10;
    this.current_chart.options.scales.y.suggestedMax = 1;

    // Graphs that pin a 0 floor override the -10 default just restored.
    this.applyForcedZeroYAxisFloorIfNeeded();
  }

  // Hard-pin the Y-axis floor to 0 for graphs that must never render negative
  // (Accumulated Water Input, via forceZeroYAxisMin). A hard min:0 is immune to
  // suggestedMin clobbering, updateYAxisBounds padding, and Chart.js nice-tick
  // rounding. No-op for every other graph, so it is safe to call on all paths.
  applyForcedZeroYAxisFloorIfNeeded() {
    const floor = resolveForcedZeroFloor(this.props.forceZeroYAxisMin);
    if (floor && this.current_chart) {
      this.current_chart.options.scales.y.min = floor.min;
      this.current_chart.options.scales.y.suggestedMin = floor.suggestedMin;
    }
  }

  // Check if field has changed and reset graph if necessary
  checkAndHandleFieldChange(newFieldId) {
    if (this.currentFieldId !== null && this.currentFieldId !== newFieldId) {
      // Field has changed, reset the graph
      this.resetGraphForFieldChange();
    }
    this.currentFieldId = newFieldId;
  }

  // Reset graph state when switching fields
  resetGraphForFieldChange() {
    if (this.current_chart) {
      // Reset zoom state
      this.current_chart.resetZoom();

      // Reset sensor ranges to full data
      this.resetSensorsRanges();

      // Clear any manually set x-axis bounds
      delete this.current_chart.options.scales.x.min;
      delete this.current_chart.options.scales.x.max;

      // Reset y-axis bounds
      this.resetYAxisBounds();
    }
  }

  componentDidUpdate(prevProps) {
    // Check if selectedField has changed
    const prevFieldId = prevProps.selectedField?.id;
    const currentFieldId = this.props.selectedField?.id;

    if (prevFieldId !== currentFieldId && currentFieldId) {
      this.checkAndHandleFieldChange(currentFieldId);
    }
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
    window.removeEventListener('resize', this.updateOverlaySize);
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._deferredDrawRAF) {
      cancelAnimationFrame(this._deferredDrawRAF);
    }
    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF);
      this._resizeRAF = null;
    }
  }

  isMobile() {
    return window.innerWidth < 768;
  }

  setupChart() {
    const { t } = this.props;

    var that = this;
    let options = {
      maintainAspectRatio: false,
      responsive: true,
      tension: 0.3,
      layout: {
        padding: {
          left: 0,
          // Right padding gives the rightmost X-axis tick label (drawn
          // at chartArea.right - 3 with textAlign 'right') room to sit
          // without bumping into the chart frame / navigation column.
          right: 12,
          top: 0,
          bottom: !that.props.shouldDrawBottom ? 58 : 125,
        },
      },
      hover: {
        mode: that.props.toolTipMode,
        intersect: false,
      },
      chartArea: {
        backgroundColor: '#FFFFFF',
      },
      scales: {
        x: {
          grid: {
            lineWidth: 1,
            // Vertical gridlines are hidden — only the horizontal (y-axis)
            // gridlines are drawn inside the plot area.
            display: false,
            color: '#dce0e8',
            drawBorder: false,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            source: 'data',
            display: false,
            font: {
              weight: '600',
            },
            maxTicksLimit: 20,
          },
          type:
            that.props.distributionMode === 'series' ? 'timeseries' : 'time',
          distribution: that.props.distributionMode,
          time: {
            unit: 'hour',
          },
          min: that.props.shouldSetMinMaxXAxes
            ? dayjs().subtract(17, 'd').toDate()
            : null,
        },
        y: {
          grid: {
            lineWidth: 1,
            display: true,
            color: ({ tick }) => (tick.value === 0 ? '#919db4' : '#dce0e8'),
            drawBorder: false,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            font: {
              family: 'Source Sans Pro, sans-serif',
              weight: '400',
              size: 14,
            },
            color: '#626e84',
            display: true,
            sampleSize: 3,
            padding: 4,
            autoSkip: true,
            autoSkipPadding: 6,
            maxTicksLimit: 11,
            callback: function (value, index, values) {
              if (value >= 10000) {
                return (value / 1e3).toFixed() + 'K';
              } else if (value >= 1000) {
                return (value / 1e3).toFixed(1) + 'K';
              }

              // With a hard 0 floor the 0 baseline is the first tick; keep its
              // label instead of blanking it (other graphs are unaffected).
              if (
                shouldKeepZeroBaselineTick(value, that.props.forceZeroYAxisMin)
              ) {
                return value;
              }

              if (index != 0 && index != values.length - 1) {
                return value;
              } else {
                return '';
              }
            },
          },
          title: {
            font: {
              family: 'Source Sans Pro, sans-serif',
              weight: '400',
              size: 14,
            },
            color: '#626e84',
            display: true,
            text: upper(that.props.units),
          },
          beginAtZero: true,
          suggestedMax: 1,
          // Graphs with strictly non-negative data (Accumulated Water Input)
          // pin a hard 0 floor via forceZeroYAxisMin; everyone else keeps the
          // shared -10 default (Water Potential needs the negative range).
          suggestedMin: that.props.forceZeroYAxisMin ? 0 : -10,
          min: that.props.forceZeroYAxisMin ? 0 : undefined,
        },
      },
      animation: false,
      animations: {
        colors: false,
        numbers: false,
      },
      transitions: {
        resize: {
          animation: { duration: 0 },
        },
        active: {
          animation: { duration: 0 },
        },
      },
      plugins: {
        tooltip: {
          mode: that.props.toolTipMode,
          intersect: false,
          caretPadding: 15,
          position: 'nearest',
          yAlign: computeTooltipYAlign,
          backgroundColor: '#4d5674',
          bodySpacing: 5,
          callbacks: {
            title: function (tooltipItems) {
              var date = tooltipItems[0].raw.x;
              return that.props.shouldDiplayUTC
                ? dayjs(date).tz(that.timezone).format('DD/MM/YYYY HH:mm UTCZ')
                : dayjs(date).tz(that.timezone).format('DD/MM/YYYY HH:mm');
            },
            label: function (tooltipItem, data) {
              let label = tooltipItem.dataset.transmitterID;
              let labels = [];
              if (label != tooltipItem.dataset.displayName) {
                label = tooltipItem.dataset.displayName || '';
              }
              if (label) {
                label += ': ';
                label += Math.round(tooltipItem.raw.y * 100) / 100;
                label += ' ' + that.props.units;
                labels.push(label);
              }
              if (tooltipItem.raw.dataToDisplay?.length) {
                for (let i = 0; i < tooltipItem.raw.dataToDisplay.length; i++) {
                  if (tooltipItem.raw.dataToDisplay[i].length) {
                    labels.push(tooltipItem.raw.dataToDisplay[i]);
                  }
                }
              }
              return labels;
            },
          },
        },
        legend: {
          display: false,
        },
        annotation: {
          common: {
            drawTime: 'afterDatasetsDraw',
          },
          annotations: {
            'a-line-1': {
              drawTime: 'afterDraw',
              id: 'a-line-1',
              type: 'line',
              scaleID: 'y',
              value: that.props.irrigation_hin,
              borderColor:
                that.props.irrigation_hin > 0
                  ? 'rgba(246, 107, 122, 0.3)'
                  : 'transparent',
              borderWidth: 2,
              label: {
                backgroundColor: 'rgba(252, 211, 215, 1)',
                font: {
                  size: 12,
                  weight: 700,
                  color: 'rgba(252, 211, 215, 1)',
                },
                padding: {
                  x: 4,
                  y: 4,
                },
                borderRadius: 3,
                position: 'start',
                xAdjust: 6,
                yAdjust: 0,
                display: that.props.irrigation_hin > 0 ? true : false,
                content: `Hin: ${that.props.irrigation_hin}`,
              },
            },
            'a-line-2': {
              drawTime: 'afterDraw',
              id: 'a-line-2',
              type: 'line',
              scaleID: 'x',
              value: dayjs().toDate(),
              borderColor: that.props.shouldDrawTodayMark
                ? 'rgb(246,107,122,0.30)'
                : 'transparent',
              borderWidth: 3,
              label: {
                display: that.props.shouldDrawTodayMark ? true : false,
                backgroundColor: 'transparent',
                font: {
                  size: 12,
                  style: 'normal',
                  weight: 'normal',
                },
                color: '#919DB4',
                position: 'end',
                xAdjust: 3,
                yAdjust: 7,
                content: 'Today',
                borderColor: 'transparent',
                textAlign: 'end',
                borderWidth: 0,
              },
            },
          },
        },
        zoom: {
          zoom: {
            drag: {
              enabled: that.props.shouldDisplayZoom,
            },
            mode: 'x',
            onZoomComplete: ({ chart }) => {
              if (chart.isZoomedOrPanned()) {
                const { min, max } = chart.scales.x;
                this.changeSensorsRangesBasedOnDateRange(
                  dayjs(min),
                  dayjs(max),
                );
                this.updateChartAfterZoomChanges();
              }
            },
          },
        },
      },
    };

    var data_info = {
      datasets: [
        {
          label_indexes: [],
          type: 'line',
          label: 'line graph',
          fill: 'true',
          borderColor: '#4251b9',
          backgroundColor: 'rgba(0,0,0,0.0)',
          pointBackgroundColor: 'rgba(0,0,0,0.7)',
          pointRadius: 0,
          pointHitRadius: 8,
          borderWidth: 2,
          data: [],
        },
      ],
    };

    var plugins = [
      {
        id: 'guard-events-outside-chart-area',
        // The unified white card now extends 18 px below chartArea to
        // wrap the irrigation/rain tray. Clicks/hovers there still
        // reach the canvas and Chart.js's `intersect:false, mode:nearest`
        // tooltip then tries to look up a data point and crashes inside
        // LineController.getLabelAndValue when a dataset is sparse.
        // Drop pointer events that land outside chartArea so the
        // tooltip path never runs there.
        beforeEvent: (chart, args) => {
          const event = args.event;
          if (!event || event.type === 'mouseout') return;
          const ca = chart.chartArea;
          if (!ca) return;
          const x = event.x;
          const y = event.y;
          if (x == null || y == null) return;
          if (x < ca.left || x > ca.right || y < ca.top || y > ca.bottom) {
            return false;
          }
        },
      },
      {
        id: 'annotation-plugin-id',
        // vertical line for hover on graph
        afterDatasetsDraw: (chart) => {
          // Mask the four corners so X/Y grid lines that reach the
          // rectangular chartArea corners don't leak past the rounded
          // white card. Drawing a "frame" shape with the even-odd fill
          // rule paints only the area between the chartArea rectangle
          // and the rounded rectangle (i.e. the corner crescents),
          // using the gray body color. Then stroke the rounded border
          // again so the outline is clean.
          if (!chart.config.options.chartArea?.backgroundColor) return;
          const ctx = chart.ctx;
          const { left, top, right } = chart.chartArea;
          // Card extends 18 px below chartArea.bottom to wrap the tray
          // (irrigation + rain bar rows) — only on graphs that draw
          // event inputs. Otherwise the bottom of the card sits flush
          // with chartArea.bottom. Keep in sync with beforeDraw.
          const TRAY_HEIGHT = that.props.shouldDrawInputs ? 18 : 0;
          const bottom = chart.chartArea.bottom + TRAY_HEIGHT;
          const chartBottom = chart.chartArea.bottom;
          const radius = 8;
          // Mask the TOP-left and TOP-right corners only. Grid lines
          // reach the rectangular chartArea corners and would otherwise
          // leak past the rounded white card. The bottom of the unified
          // card sits below chartArea, so its rounded bottom corners
          // don't intersect any grid and don't need masking.
          const PAD = 2; // overdraw a couple pixels to cover anti-aliasing
          ctx.save();
          ctx.beginPath();
          // Outer rect stops at chartBottom — don't pad downward, the
          // tray below chartBottom must stay white.
          ctx.rect(
            left - PAD,
            top - PAD,
            right - left + 2 * PAD,
            chartBottom - top + PAD,
          );
          ctx.moveTo(left + radius, top);
          ctx.lineTo(right - radius, top);
          ctx.quadraticCurveTo(right, top, right, top + radius);
          ctx.lineTo(right, chartBottom);
          ctx.lineTo(left, chartBottom);
          ctx.lineTo(left, top + radius);
          ctx.quadraticCurveTo(left, top, left + radius, top);
          ctx.closePath();
          ctx.fillStyle = '#f6f8fa';
          ctx.fill('evenodd');
          ctx.restore();
          // Stroke the unified rounded card outline (chart + tray) on
          // top so it sits above the grid lines and the corner mask.
          // No internal divider at chartBottom — the white fill flows
          // straight through.
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(left + radius, top);
          ctx.lineTo(right - radius, top);
          ctx.quadraticCurveTo(right, top, right, top + radius);
          ctx.lineTo(right, bottom - radius);
          ctx.quadraticCurveTo(right, bottom, right - radius, bottom);
          ctx.lineTo(left + radius, bottom);
          ctx.quadraticCurveTo(left, bottom, left, bottom - radius);
          ctx.lineTo(left, top + radius);
          ctx.quadraticCurveTo(left, top, left + radius, top);
          ctx.closePath();
          ctx.lineWidth = 1;
          ctx.strokeStyle = '#dce0e8';
          ctx.stroke();
          ctx.restore();
        },
        afterDraw: (chart) => {
          if (chart.tooltip?._active?.length) {
            let x = chart.tooltip._active[0].element.x;
            let yAxis = chart.scales.y;
            let ctx = chart.ctx;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x, yAxis.top);
            ctx.lineTo(x, yAxis.bottom);
            ctx.lineWidth = 1;
            ctx.strokeStyle = '#919DB4';
            ctx.stroke();
            ctx.restore();
          }
        },
        beforeDraw: function (chart, easing) {
          var chartArea = chart.chartArea;
          var ctx = chart.ctx;

          // set defaults
          ctx.textBaseline = 'alphabetic';
          ctx.textAlign = 'start';

          // Fill the chartarea with background according to the option parameter
          if (chart.config.options.chartArea?.backgroundColor) {
            const left = chartArea.left;
            const top = chartArea.top;
            const right = chartArea.right;
            // Unified rounded card that wraps BOTH the chart plot and the
            // 18 px tray that holds the irrigation/rain event bars
            // below. No internal divider — the white fill carries
            // through, and the 1px border (drawn in afterDatasetsDraw)
            // outlines the whole shape. Skip the tray on graphs that
            // don't render event bars (shouldDrawInputs=false), so the
            // date labels below sit against the gray frame instead of
            // being trapped inside the card.
            const TRAY_HEIGHT = that.props.shouldDrawInputs ? 18 : 0;
            const bottom = chartArea.bottom + TRAY_HEIGHT;
            const radius = 8;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(left + radius, top);
            ctx.lineTo(right - radius, top);
            ctx.quadraticCurveTo(right, top, right, top + radius);
            ctx.lineTo(right, bottom - radius);
            ctx.quadraticCurveTo(right, bottom, right - radius, bottom);
            ctx.lineTo(left + radius, bottom);
            ctx.quadraticCurveTo(left, bottom, left, bottom - radius);
            ctx.lineTo(left, top + radius);
            ctx.quadraticCurveTo(left, top, left + radius, top);
            ctx.closePath();
            ctx.fillStyle = chart.config.options.chartArea.backgroundColor;
            ctx.fill();
            ctx.restore();
          }

          if (that.props.shouldDrawBottom) {
            // bottom left side
            ctx.fillStyle = '#f6f8fa';
            ctx.fillRect(chartArea.left - 100, chartArea.bottom + 27, 100, 109);
            // left side
            ctx.fillStyle = '#f6f8fa';
            ctx.fillRect(
              chartArea.left - 100,
              chartArea.top,
              100,
              chartArea.bottom - chartArea.top + 29,
            ); // 109
          }

          // bottom
          if (that.props.shouldDrawBottom) {
            ctx.fillStyle = '#f6f8fa';
            ctx.fillRect(
              chartArea.left,
              chartArea.bottom + 27,
              chartArea.right - chartArea.left,
              109,
            );
          } else {
            ctx.fillStyle = '#f6f8fa';
            ctx.fillRect(
              chartArea.left,
              chartArea.bottom,
              chartArea.right - chartArea.left,
              29,
            );
            // left side
            ctx.fillRect(
              chartArea.left - 100,
              chartArea.top,
              100,
              chartArea.bottom - chartArea.top,
            ); // 109
          }

          // Only draw the Min / Max / ETc row labels when the daily data
          // (temperature_h / baseline_etc_by_kc_table) is actually loaded.
          // While historical_data is still pending, hide the rows
          // entirely so the panel doesn't show empty headers.
          const hasTempData =
            that.dailyData?.temperature_h?.values &&
            Object.keys(that.dailyData.temperature_h.values).length > 0;
          const hasEtcData =
            that.dailyData?.baseline_etc_by_kc_table?.values &&
            Object.keys(that.dailyData.baseline_etc_by_kc_table.values).length >
              0;
          const hasDailyData = hasTempData || hasEtcData;

          if (that.props.shouldDrawBottom && hasDailyData) {
            // Min/Max/ETc titles — Figma: Source Sans Pro 400 14px #626e84
            // Rows are 20px stacked (was 25px), giving a tighter group.
            ctx.font = '14px "Source Sans Pro", sans-serif';
            ctx.fillStyle = '#626e84';
            ctx.fillText(
              t('minimum'),
              chartArea.left - 40,
              chart.chartArea.bottom + 72,
            );
            ctx.fillText(
              t('maximum'),
              chartArea.left - 40,
              chart.chartArea.bottom + 72 + 20,
            );
            ctx.fillText(
              t('etc'),
              chartArea.left - 40,
              chart.chartArea.bottom + 72 + 40,
            );
          }

          var xAxis = chart.scales['x'];
          var chartWidth = chart.chartArea.right - chart.chartArea.left;
          var chartHeight = chart.chartArea.bottom - chart.chartArea.top;

          // Skip expensive DOM rebuilds and dayjs calls when nothing changed
          // (e.g. tooltip hovers, animation frames)
          var rangeChanged =
            xAxis.min !== that._cachedXMin ||
            xAxis.max !== that._cachedXMax ||
            chartWidth !== that._cachedChartWidth ||
            chartHeight !== that._cachedChartHeight ||
            that.dailyData !== that._cachedDailyDataRef;

          if (!rangeChanged && that._cachedTickLabels) {
            that._drawCachedTickLabels(chart);
            return;
          }

          // Keep the previous labels visible while a resize-triggered rebuild
          // is pending so Advanced/Close toggles don't flash blank text.
          if (rangeChanged && that._cachedTickLabels) {
            that._drawCachedTickLabels(chart);
          }

          // Defer heavy overlay/label work to next frame so the chart lines
          // paint immediately and the main thread stays responsive.
          if (that._deferredDrawRAF) {
            cancelAnimationFrame(that._deferredDrawRAF);
          }
          that._deferredDrawRAF = requestAnimationFrame(() => {
            that._deferredDrawRAF = null;
            if (!that.current_chart) {
              return;
            }
            that._buildOverlaysAndLabels(chart);
            // Read dimensions after rebuild — the beforeDraw closure can be
            // stale if chart.resize() finished later in the same turn.
            var xAxisNow = chart.scales['x'];
            var area = chart.chartArea;
            that._cachedXMin = xAxisNow.min;
            that._cachedXMax = xAxisNow.max;
            that._cachedChartWidth = area.right - area.left;
            that._cachedChartHeight = area.bottom - area.top;
            that._cachedDailyDataRef = that.dailyData;
            chart.draw();
          });
        },
      },
    ];

    var all = {
      type: 'line',
      data: data_info,
      options: options,
      plugins: plugins,
    };

    var canvas = document.getElementById('chartJSContainer');
    var ctx = canvas.getContext('2d');

    this.current_chart = new Chart(ctx, all);

    var overlay = document.getElementById('overlay');

    this.updateOverlaySize();

    var selectionContext = overlay.getContext('2d');
    selectionContext.fillStyle = '#919DB4';
  }

  updateOverlaySize() {
    const el = this._chartWrapperRef.current;
    const width = el ? Math.round(el.clientWidth) : null;
    const height = el ? Math.round(el.clientHeight) : null;
    this._applyChartContainerResize(width, height);
  }

  // Deferred from beforeDraw — builds event overlay DOM and tick labels
  // in a separate animation frame so Chart.js line drawing isn't blocked.
  _buildOverlaysAndLabels(chart) {
    var that = this;
    var xAxis = chart.scales['x'];
    var chartWidth = chart.chartArea.right - chart.chartArea.left;

    var ticksGapInSeconds =
      xAxis.ticks.length > 0
        ? (xAxis.ticks[xAxis.ticks.length - 1]?.value - xAxis.ticks[0]?.value) /
          1000
        : 0;
    var totalPixelsInChart = chartWidth;

    var additionals = document.getElementById('additionals');
    if (additionals) {
      additionals.textContent = '';
      var fragment = document.createDocumentFragment();

      var graphStartDate = xAxis.min
        ? xAxis.min / 1000
        : xAxis.ticks.length > 0
          ? xAxis.ticks[0]?.value / 1000
          : 0;
      var graphEndDate = xAxis.max
        ? xAxis.max / 1000
        : xAxis.ticks.length > 0
          ? xAxis.ticks[xAxis.ticks.length - 1]?.value / 1000
          : 0;
      var yPos = chart.chartArea.bottom;
      // Opt-in flag for the redesigned tray popup + pressure-alert badge. Only
      // the Water Potential graph passes it today; every other graph keeps the
      // legacy popup untouched.
      var useNewEventDesign = !!that.props.shouldUseNewEventDesign;

      if (that.props.shouldDrawInputs && that.dailyData) {
        // Render all WFR events
        if ('wfr' in that.dailyData && that.dailyData.wfr.values) {
          Object.keys(that.dailyData.wfr.values).forEach(
            function (wfrStartDate) {
              let wfrObject = that.dailyData.wfr.values[wfrStartDate];
              let eventStartTimestamp = parseInt(wfrStartDate);

              if (
                wfrObject &&
                graphStartDate < wfrObject.endDate &&
                graphEndDate >= eventStartTimestamp
              ) {
                let eventXPosMs = eventStartTimestamp * 1000;
                let eventXPos = xAxis.getPixelForValue(eventXPosMs);

                let eventStartsBeforeVisibleRange =
                  eventStartTimestamp < graphStartDate;
                let effectiveStartTimestamp = eventStartTimestamp;

                if (eventStartsBeforeVisibleRange) {
                  eventXPos = chart.chartArea.left;
                  effectiveStartTimestamp = graphStartDate;
                }

                if (eventXPos > chart.chartArea.right + 50) {
                  return;
                }

                let irrigationLength =
                  wfrObject.endDate - effectiveStartTimestamp;
                let proportion = irrigationLength / ticksGapInSeconds;
                let rawWfrLength = totalPixelsInChart * proportion;
                let barGeometry = clampEventBarGeometry(
                  rawWfrLength,
                  eventXPos,
                  chart.chartArea,
                );
                if (!barGeometry) {
                  return;
                }
                eventXPos = barGeometry.left;
                let wfrLength = barGeometry.width;

                let rightMenuClass =
                  chart.chartArea.right - eventXPos > 150
                    ? ''
                    : 'dropdown-menu-right';

                // Everything else about the event (amount, pressure, alert
                // level, delete permission) is derived in
                // buildIrrigationPopup, which the grouped popup reuses.
                let unitText = that.dailyData.wfr_length?.units || '';

                var currentChild = document.createElement('div');
                let bar = document.createElement('div');
                bar.className =
                  'dropdown-hover bg-blue-secondary position-absolute';
                bar.style.cssText =
                  'height:9px; width:' +
                  wfrLength +
                  'px; left:' +
                  eventXPos +
                  'px; top:' +
                  yPos +
                  'px';

                let toggle = document.createElement('a');
                toggle.href = '#';
                toggle.setAttribute('data-toggle', 'dropdown');
                toggle.setAttribute('role', 'button');
                toggle.setAttribute('aria-haspopup', 'true');
                toggle.setAttribute('aria-expanded', 'false');
                toggle.textContent = ' ';

                let menu = document.createElement('div');
                menu.className =
                  (useNewEventDesign
                    ? NEW_POPUP_MENU_CLASSES
                    : LEGACY_POPUP_MENU_CLASSES) + rightMenuClass;
                menu.style.cssText = useNewEventDesign
                  ? NEW_POPUP_MENU_STYLE
                  : LEGACY_POPUP_MENU_STYLE;

                bar.appendChild(toggle);
                bar.appendChild(menu);
                // Every flagged event carries its own badge and its own
                // single-event popup.
                let alertLevel = useNewEventDesign
                  ? pressureAlertLevel(
                      that.dailyData.wfr_length?.values?.[wfrStartDate]
                        ?.pressure_alert,
                    )
                  : null;
                let alertBadge = alertLevel ? buildPressureAlertBadge() : null;
                if (alertBadge) {
                  bar.appendChild(alertBadge);
                }
                currentChild.appendChild(bar);

                // Built on first hover (and cached per mode) rather than up
                // front — dayjs().tz() formatting per event would freeze the
                // tray on a wide date range.
                let popupHtmlByMode = {};
                let buildPopupHtml = function (mode) {
                  if (!popupHtmlByMode[mode]) {
                    popupHtmlByMode[mode] = that.buildIrrigationPopup(
                      parseInt(wfrStartDate),
                      unitText,
                      {
                        useNewDesign: useNewEventDesign,
                        showArrow: mode === 'above',
                      },
                    );
                  }
                  return popupHtmlByMode[mode];
                };

                // 'below' is the ordinary dropdown shown when the bar is
                // hovered. Hovering the pressure-alert badge switches to
                // 'above' so the card clears the icon and its caret points at
                // it. Reverting happens on leaving the bar entirely, not on
                // leaving the badge — otherwise moving the pointer from the
                // badge into the card would drop the card out from under it.
                let popupMode = null;
                let showPopup = function (mode) {
                  if (popupMode === mode) return;
                  popupMode = mode;
                  applyPopupMode(menu, mode, rightMenuClass, alertBadge);
                  menu.innerHTML = buildPopupHtml(mode);
                  if (mode === 'above' && alertBadge) {
                    capBadgePopupHeight(menu, alertBadge);
                  }
                };

                bar.addEventListener('mouseenter', function () {
                  if (!useNewEventDesign) {
                    if (menu.childNodes.length > 0) return;
                    menu.innerHTML = buildPopupHtml('below');
                    return;
                  }
                  showPopup('below');
                });

                if (alertBadge) {
                  alertBadge.addEventListener('mouseenter', function () {
                    showPopup('above');
                  });
                  bar.addEventListener('mouseleave', function () {
                    showPopup('below');
                  });
                }

                currentChild.onclick = function (evt) {
                  if (evt) {
                    evt.preventDefault();
                    evt.stopPropagation();
                  }
                  if (evt.srcElement.id == 'deleteIrrigationButton') {
                    let date = evt.srcElement.attributes.date?.value / 1000.0;
                    let disableEventRecovery = /true/.test(
                      evt.srcElement.attributes.disableeventrecovery.value,
                    );
                    let sensorId = evt.srcElement.attributes.sensorid.value;
                    fieldManager.deleteIrrigationEvent(
                      { startDate: date, source: sensorId },
                      disableEventRecovery,
                      that.props.selectedField,
                    );
                    that.props.getFieldHistory(
                      that.props.selectedDistribution.id,
                      that.props.selectedFarmingUnit.id,
                      that.props.selectedField.id,
                    );
                  }
                };

                fragment.appendChild(currentChild);
              }
            },
          );
        }

        // Render all Rain events
        if ('precipitation_h' in that.dailyData && that.rainSamples) {
          Object.keys(that.rainSamples).forEach(function (rainStartDate) {
            let rainObject = that.rainSamples[rainStartDate];
            let eventStartTimestamp = parseInt(rainStartDate);

            if (
              rainObject &&
              graphStartDate < rainObject.endDate &&
              graphEndDate >= eventStartTimestamp
            ) {
              let rainEventStartTimestamp = eventStartTimestamp * 1000;
              let rainEventXPos = xAxis.getPixelForValue(
                rainEventStartTimestamp,
              );

              let rainStartsBeforeVisibleRange =
                eventStartTimestamp < graphStartDate;
              let effectiveRainStartTimestamp = eventStartTimestamp;

              if (rainStartsBeforeVisibleRange) {
                rainEventXPos = chart.chartArea.left;
                effectiveRainStartTimestamp = graphStartDate;
              }

              if (rainEventXPos > chart.chartArea.right + 50) {
                return;
              }

              let rainLength = rainObject.endDate - effectiveRainStartTimestamp;
              let proportion = rainLength / ticksGapInSeconds;
              let rawRainLength = totalPixelsInChart * proportion;
              let rainBarGeometry = clampEventBarGeometry(
                rawRainLength,
                rainEventXPos,
                chart.chartArea,
              );
              if (!rainBarGeometry) {
                return;
              }
              rainEventXPos = rainBarGeometry.left;
              let precipitationLength = rainBarGeometry.width;

              let rainPrecipitationValue = rainObject?.value;
              let unitText = unitDisplayName(
                that.dailyData.precipitation_h.units,
              );
              let value = rainPrecipitationValue.toFixed(2).toString();
              let enableDelete =
                rainObject.source === 'manual' || that.props?.hasPermission;

              let rightMenuClass =
                chart.chartArea.right - rainEventXPos > 150
                  ? ''
                  : 'dropdown-menu-right';

              var currentChild = document.createElement('div');
              let bar = document.createElement('div');
              bar.className =
                'dropdown dropdown-hover bg-blue-third position-absolute';
              bar.style.cssText =
                'height:9px; width:' +
                precipitationLength +
                'px; left:' +
                rainEventXPos +
                'px; top:' +
                (yPos + 9) +
                'px';

              let toggle = document.createElement('a');
              toggle.href = '#';
              toggle.setAttribute('data-toggle', 'dropdown');
              toggle.setAttribute('role', 'button');
              toggle.setAttribute('aria-haspopup', 'true');
              toggle.setAttribute('aria-expanded', 'false');
              toggle.textContent = ' ';

              let menu = document.createElement('div');
              menu.className =
                (useNewEventDesign
                  ? NEW_POPUP_MENU_CLASSES
                  : LEGACY_POPUP_MENU_CLASSES) +
                rightMenuClass +
                ' bg-white';
              menu.style.cssText = useNewEventDesign
                ? NEW_POPUP_MENU_STYLE
                : LEGACY_POPUP_MENU_STYLE;

              bar.appendChild(toggle);
              bar.appendChild(menu);
              currentChild.appendChild(bar);

              bar.addEventListener('mouseenter', function () {
                if (menu.childNodes.length > 0) return;
                let beginning = dayjs.unix(rainStartDate).tz(that.timezone);
                let end = dayjs.unix(rainObject.endDate).tz(that.timezone);
                menu.innerHTML = EventPopUp(
                  beginning,
                  end,
                  'rain',
                  unitText,
                  value,
                  rainObject,
                  enableDelete,
                  'sensorGraph',
                  that.timezone,
                  that.props.t,
                  '',
                  null,
                  '',
                  { useNewDesign: useNewEventDesign },
                );
              });
              currentChild.onclick = function (evt) {
                if (evt) {
                  evt.preventDefault();
                  evt.stopPropagation();
                }
                if (evt.srcElement.id == 'deletePrecipitationButton') {
                  let date = evt.srcElement.attributes.date?.value / 1000.0;
                  let source = evt.srcElement.attributes.source.value;
                  that.props.onPrecipitationDeletion(date, source);
                }
              };
              fragment.appendChild(currentChild);
            }
          });
        }

        // Render all Fertilizer events
        if ('fert' in that.dailyData && that.dailyData.fert.values) {
          Object.keys(that.dailyData.fert.values).forEach(
            function (fertStartDate) {
              let fertObj = that.dailyData.fert.values[fertStartDate];
              let eventStartTimestamp = parseInt(fertStartDate);

              if (
                fertObj &&
                graphStartDate < eventStartTimestamp + 60 * 60 * 24 &&
                graphEndDate >= eventStartTimestamp
              ) {
                let nitrogenInFertUnits = that.dailyData.fert.units;
                let unitsDisplayNameLabel = unitDisplayName(fertObj['units']);

                let fertEventStartTimestamp = eventStartTimestamp * 1000;
                let fertEventXPos = xAxis.getPixelForValue(
                  fertEventStartTimestamp,
                );

                let fertStartsBeforeVisibleRange =
                  eventStartTimestamp < graphStartDate;
                let effectiveFertStartTimestamp = eventStartTimestamp;

                if (fertStartsBeforeVisibleRange) {
                  fertEventXPos = chart.chartArea.left;
                  effectiveFertStartTimestamp = graphStartDate;
                }

                if (fertEventXPos > chart.chartArea.right + 50) {
                  return;
                }

                let fertDuration = fertObj.endDate
                  ? parseInt(fertObj.endDate) - effectiveFertStartTimestamp
                  : 60 * 60 * 4;
                let proportion = fertDuration / ticksGapInSeconds;
                let rawFertLength = totalPixelsInChart * proportion;
                let fertBarGeometry = clampEventBarGeometry(
                  rawFertLength,
                  fertEventXPos,
                  chart.chartArea,
                );
                if (!fertBarGeometry) {
                  return;
                }
                fertEventXPos = fertBarGeometry.left;
                let fertLength = fertBarGeometry.width;

                let rightMenuClass =
                  chart.chartArea.right - fertEventXPos > 150
                    ? ''
                    : 'dropdown-menu-right';
                let unitText = unitsDisplayNameLabel;
                let value = fertObj.nitroAmount.toFixed(2).toString();
                fertObj.fertAmount = fertObj.value;

                var currentChild = document.createElement('div');
                let bar = document.createElement('div');
                bar.className =
                  'dropdown dropdown-hover bg-green2 position-absolute';
                bar.style.cssText =
                  'height:9px; width:' +
                  fertLength +
                  'px; left:' +
                  fertEventXPos +
                  'px; top:' +
                  (yPos + 18) +
                  'px';

                let toggle = document.createElement('a');
                toggle.href = '#';
                toggle.setAttribute('data-toggle', 'dropdown');
                toggle.setAttribute('role', 'button');
                toggle.setAttribute('aria-haspopup', 'true');
                toggle.setAttribute('aria-expanded', 'false');
                toggle.textContent = ' ';

                let menu = document.createElement('div');
                menu.className =
                  (useNewEventDesign
                    ? NEW_POPUP_MENU_CLASSES
                    : LEGACY_POPUP_MENU_CLASSES) +
                  rightMenuClass +
                  ' bg-white';
                menu.style.cssText = useNewEventDesign
                  ? NEW_POPUP_MENU_STYLE
                  : LEGACY_POPUP_MENU_STYLE;

                bar.appendChild(toggle);
                bar.appendChild(menu);
                currentChild.appendChild(bar);

                bar.addEventListener('mouseenter', function () {
                  if (menu.childNodes.length > 0) return;
                  let beginning = dayjs.unix(fertStartDate).tz(that.timezone);
                  let end = dayjs
                    .unix(parseInt(fertStartDate) + 60 * 60 * 24)
                    .tz(that.timezone);
                  menu.innerHTML = EventPopUp(
                    beginning,
                    end,
                    'fertilization',
                    unitText,
                    value,
                    fertObj,
                    true,
                    'sensorGraph',
                    that.timezone,
                    that.props.t,
                    nitrogenInFertUnits,
                    null,
                    '',
                    { useNewDesign: useNewEventDesign },
                  );
                });

                currentChild.onclick = function (evt) {
                  if (evt) {
                    evt.preventDefault();
                    evt.stopPropagation();
                  }

                  if (evt.srcElement.id == 'deleteFertilizationButton') {
                    fieldManager.deleteFertilizationEvent(
                      {
                        startDate:
                          evt.srcElement.attributes.date?.value / 1000.0,
                      },
                      that.props.selectedField,
                    );
                    that.props.getFieldHistory(
                      that.props.selectedDistribution.id,
                      that.props.selectedFarmingUnit.id,
                      that.props.selectedField.id,
                    );
                  }
                };

                fragment.appendChild(currentChild);
              }
            },
          );
        }
      }

      additionals.appendChild(fragment);
    }

    // Paint tick labels (min/max/ETc) on the canvas
    this._computeTickLabels(chart);
    this._drawCachedTickLabels(chart);
  }

  _computeTickLabels(chart) {
    var xAxis = chart.scales['x'];

    // Accumulated graph: use a fixed-step, evenly spaced date grid instead of
    // the per-sample tick labels so date-to-date pixel distance is constant.
    if (this.props.alignDatesToDayBoundary && this.timezone) {
      this._cachedTickLabels = this._computeEvenlySpacedDateLabels(chart);
      return;
    }

    var chartArea = chart.chartArea;
    var yPos = chartArea.bottom;
    var tickGap = xAxis.getPixelForTick(1) - xAxis.getPixelForTick(0);
    var labels = [];
    var pointsArray = [];

    // Compute timezone offset once using integer math for day-boundary detection
    var tzOffsetMs = 0;
    var MS_PER_DAY = 86400000;
    if (this.timezone && xAxis.ticks.length > 0) {
      tzOffsetMs =
        dayjs(xAxis.ticks[0]?.value).tz(this.timezone).utcOffset() * 60000;
    }
    var prevDayNum = -1;
    var hasData =
      this.dailyData && this.etcStatistics && this.temperatureStatistics;
    var etcUnits = hasData
      ? unitDisplayName(this.dailyData.baseline_etc_by_kc_table?.units)
      : '';
    var tempUnits = hasData
      ? unitDisplayName(this.dailyData.temperature_h?.units)
      : '';

    helpers.each(xAxis.ticks, (tick, index) => {
      let etcValue = '';
      let minTempValue = '';
      let maxTempValue = '';
      let dayPassed = false;

      // Detect day boundary with integer math instead of dayjs per tick
      var tickMs = xAxis.ticks[index]?.value;
      var currentDayNum = Math.floor((tickMs + tzOffsetMs) / MS_PER_DAY);

      if (hasData && currentDayNum !== prevDayNum) {
        prevDayNum = currentDayNum;
        dayPassed = true;

        // Only call dayjs for the few ticks that produce labels
        var current_date = dayjs(tickMs).tz(this.timezone);
        let todayTimeStampMidnight = current_date.startOf('day').unix();
        let todayTimeStampMidnightETc = current_date
          .add(1, 'days')
          .startOf('day')
          .unix();

        if (todayTimeStampMidnightETc in this.etcStatistics) {
          etcValue =
            this.etcStatistics[todayTimeStampMidnightETc]['lastValue'] +
            etcUnits;
        }

        if (todayTimeStampMidnight in this.temperatureStatistics) {
          // Round Min/Max to the nearest 0.5 — Figma shows values like
          // "21.5°C" / "22°C" rather than the raw 2-decimal source data.
          var roundHalf = function (v) {
            var n = parseFloat(v);
            if (isNaN(n)) return v;
            return String(Math.round(n * 2) / 2);
          };
          maxTempValue =
            roundHalf(
              this.temperatureStatistics[todayTimeStampMidnight]['maxValue'],
            ) + tempUnits;
          minTempValue =
            roundHalf(
              this.temperatureStatistics[todayTimeStampMidnight]['minValue'],
            ) + tempUnits;
        }
      } else {
        prevDayNum = currentDayNum;
      }

      var xPos = xAxis.getPixelForTick(index);
      var tickYPos = yPos;

      let filteredArray = pointsArray.filter(
        (currentPoint) => xPos - currentPoint.x < 75,
      );

      // Figma: Source Sans Pro 400 14px for the X-axis date labels and
      // the Min/Max/ETc per-column values.
      var font = '400 14px "Source Sans Pro", sans-serif';
      if (!this.props.shouldDrawBottom) {
        tickYPos -= 27;
      }

      // Figma: date labels and the per-column Min/Max/ETc values both
      // sit left-aligned at the tick's x position. Edge cases below
      // override this for the first/last tick so labels don't overflow.
      var textAlign = 'left';
      var tickLabel = tick;

      if (xPos - chartArea.left < 25) {
        textAlign = 'left';
        xPos = chartArea.left + 3;
        if (tick.length > 10) tickLabel = '';
      }

      if (chartArea.right - xPos < 25) {
        textAlign = 'right';
        xPos = chartArea.right - 3;
        if (tick.length > 10) tickLabel = '';
      }

      if (filteredArray.length == 0 && dayPassed && tickLabel != '') {
        pointsArray.push({ x: xPos, y: tickYPos });
        labels.push({
          xPos,
          tickYPos,
          tickGap,
          font,
          textAlign,
          label: tickLabel.label,
          minTempValue,
          maxTempValue,
          etcValue,
        });
      }
    });

    this._cachedTickLabels = labels;
  }

  // Accumulated graph only: build date labels on a fixed whole-day grid so the
  // pixel distance between every pair of dates is identical, independent of
  // sample count and of Chart.js's irregular auto-ticks (whose first tick sits
  // at the data start rather than on the regular grid).
  _computeEvenlySpacedDateLabels(chart) {
    var xAxis = chart.scales['x'];
    var chartArea = chart.chartArea;
    var MS_PER_DAY = 86400000;
    var labels = [];

    var minMs = xAxis.min != null ? xAxis.min : xAxis.ticks[0]?.value;
    var maxMs =
      xAxis.max != null
        ? xAxis.max
        : xAxis.ticks[xAxis.ticks.length - 1]?.value;
    if (minMs == null || maxMs == null) {
      return labels;
    }

    // Pixel width of one day on the linear time axis.
    var anchorMs = dayjs(minMs).tz(this.timezone).startOf('day').valueOf();
    var dayWidthPx =
      xAxis.getPixelForValue(anchorMs + MS_PER_DAY) -
      xAxis.getPixelForValue(anchorMs);
    if (!(dayWidthPx > 0)) {
      return labels;
    }

    // Whole-day step so consecutive dates sit at least ~75px apart; the gap is
    // then constant at stepDays * dayWidthPx for every pair of dates.
    var MIN_GAP_PX = 75;
    var stepDays = Math.max(1, Math.ceil(MIN_GAP_PX / dayWidthPx));

    var tickYPos = chartArea.bottom;
    if (!this.props.shouldDrawBottom) {
      tickYPos -= 27;
    }

    var dayCursor = dayjs(minMs).tz(this.timezone).startOf('day');
    for (var guard = 0; guard < 500; guard++) {
      var dayMs = dayCursor.valueOf();
      if (dayMs > maxMs) {
        break;
      }
      var xPos = xAxis.getPixelForValue(dayMs);
      var textAlign = 'left';
      if (xPos - chartArea.left < 25) {
        xPos = chartArea.left + 3;
      } else if (chartArea.right - xPos < 25) {
        textAlign = 'right';
        xPos = chartArea.right - 3;
      }
      labels.push({
        xPos: xPos,
        tickYPos: tickYPos,
        tickGap: dayWidthPx * stepDays,
        font: '400 14px "Source Sans Pro", sans-serif',
        textAlign: textAlign,
        label: dayCursor.format('DD MMM'),
        minTempValue: '',
        maxTempValue: '',
        etcValue: '',
      });
      dayCursor = dayCursor.add(stepDays, 'day');
    }
    return labels;
  }

  _drawCachedTickLabels(chart) {
    var ctx = chart.ctx;
    var labels = this._cachedTickLabels;
    if (!labels) return;

    for (var i = 0; i < labels.length; i++) {
      var entry = labels[i];
      ctx.textBaseline = 'middle';
      ctx.textAlign = entry.textAlign;

      // X-axis date label — Source Sans Pro, bold, 14px, color #626e84.
      ctx.font = '700 14px "Source Sans Pro", sans-serif';
      ctx.fillStyle = '#626e84';
      ctx.fillText(entry.label, entry.xPos, entry.tickYPos + 30 + 4 + 9);

      if (
        entry.minTempValue != '' ||
        entry.maxTempValue != '' ||
        entry.etcValue != ''
      ) {
        // Per-column Min/Max/ETc values stay regular weight (Figma 400 14px).
        ctx.font = entry.font;
        ctx.fillStyle = '#626e84';
        // Stack rows 20px apart (was 25px) — matches Figma row pitch.
        // Alignment inherits entry.textAlign (set above for the date
        // label) so values share the same left edge as the date.
        ctx.fillText(
          entry.minTempValue,
          entry.xPos,
          entry.tickYPos + 20 + 40 + 9,
        );
        ctx.fillText(
          entry.maxTempValue,
          entry.xPos,
          entry.tickYPos + 20 + 40 + 20 + 9,
        );
        ctx.fillText(
          entry.etcValue,
          entry.xPos,
          entry.tickYPos + 20 + 40 + 40 + 9,
        );
      }
    }
  }

  // One irrigation event's popup HTML, derived from the raw daily data.
  buildIrrigationPopup(startDate, unitText, options) {
    const wfrObject = this.dailyData?.wfr?.values?.[startDate];
    if (!wfrObject) {
      return '';
    }
    const lengthObject = this.dailyData?.wfr_length?.values?.[startDate];
    return EventPopUp(
      dayjs.unix(startDate).tz(this.timezone),
      dayjs.unix(wfrObject.endDate).tz(this.timezone),
      'irrigation',
      unitText,
      formatIrrigationAmount(lengthObject?.value, unitText) || '',
      wfrObject,
      wfrObject.sensor_id === 'manual' || this.props?.hasPermission,
      'sensorGraph',
      this.timezone,
      this.props.t,
      '',
      lengthObject?.pressure ?? null,
      lengthObject?.pressure_unit || '',
      {
        // Grouped entries never pass this — groups exist only in the redesign.
        useNewDesign: options.useNewDesign !== false,
        pressureAlert: pressureAlertLevel(lengthObject?.pressure_alert),
        showArrow: !!options.showArrow,
      },
    );
  }

  manipulateDailyData(dailyData) {
    var that = this;

    this.rainSamples = {};
    let rainObjects =
      dailyData && 'precipitation_h' in dailyData
        ? JSON.parse(JSON.stringify(dailyData.precipitation_h.values))
        : undefined; // we create copy of the values so we could remove values after we add them
    that.rainSamples = rainObjects || {};
  }

  loadData(
    graphSamples,
    dailyData,
    suggestedMinimum,
    timezone,
    temperatureStatistics,
    etcStatistics,
  ) {
    // Check if field has changed by looking at the selectedField prop
    const newFieldId = this.props.selectedField?.id;
    if (newFieldId) {
      this.checkAndHandleFieldChange(newFieldId);
    }

    if (!graphSamples || !dailyData || !timezone) {
      this.dailyData = {};
      this.sensorData = {};
      this.timezone = undefined;
      this.temperatureStatistics = {};
      this.etcStatistics = {};
    } else {
      this.dailyData = dailyData;
      this.sensorData = {};
      this.timezone = timezone;
      this.temperatureStatistics = temperatureStatistics;
      this.etcStatistics = etcStatistics;
      this.manipulateDailyData(dailyData);

      for (var sensorKey in graphSamples) {
        let currentSensorSample = graphSamples[sensorKey];
        if (
          currentSensorSample &&
          currentSensorSample.data &&
          currentSensorSample.data.length > 0
        ) {
          let newInstance = new SensorDataStream(
            currentSensorSample.id,
            currentSensorSample.data,
            currentSensorSample.color,
            0,
            0,
            currentSensorSample.displayName,
            currentSensorSample.transmitter_id,
          );
          this.sensorData[currentSensorSample.id] = newInstance;

          this.validateRange(newInstance, 0, newInstance.samples.length - 1);
        }
      }
    }
    this._cachedTickLabels = null;
    this.updateChart(this.sensorData, suggestedMinimum);
  }

  // Lightweight removal: remove one sensor dataset without rebuilding everything.
  // The caller (WaterPotentialGraphContainer.removeSensorData) handles Redux state
  // updates and sensor state reset.
  removeSensor(sensorId) {
    if (!this.current_chart) return;
    delete this.sensorData[sensorId];
    this.current_chart.data.datasets = this.current_chart.data.datasets.filter(
      (ds) => ds.label !== sensorId,
    );
    this._cachedTickLabels = null;
    this.current_chart.update('none');
  }

  updateDailyData(dailyData, timezone, temperatureStatistics, etcStatistics) {
    // Update only contextual daily datasets (min/max/ETc, overlays) to avoid
    // rebuilding sensor line datasets and re-fetching samples
    this.dailyData = dailyData || {};
    this.timezone = timezone;
    this.temperatureStatistics = temperatureStatistics || {};
    this.etcStatistics = etcStatistics || {};
    this.manipulateDailyData(this.dailyData);
    this._cachedTickLabels = null;
    if (this.current_chart) {
      this.current_chart.update('none');
    }
  }

  validateRange(sensorDataStream, lower_range, upper_range) {
    if (sensorDataStream && sensorDataStream.samples) {
      if (sensorDataStream.samples.length == 0) {
        sensorDataStream.lowerRange = 0;
        sensorDataStream.upperRange = 0;
      } else {
        let global_upper = sensorDataStream.samples.length - 1;
        let global_lower = 0;

        if (upper_range <= lower_range) {
          return;
        }

        sensorDataStream.upperRange = Math.min(upper_range, global_upper);
        sensorDataStream.lowerRange = Math.max(lower_range, global_lower);
      }
    }
  }

  changeSensorsRangesBasedOnDateRange(fromDate, toDate) {
    if (this.sensorData) {
      for (const sensorKey in this.sensorData) {
        let currentSensorSample = this.sensorData[sensorKey];
        let lowerRange, upperRange;
        // Ensure fromDate and toDate are dayjs objects for comparison
        const fromDateDayjs = dayjs(fromDate);
        const toDateDayjs = dayjs(toDate);
        currentSensorSample.samples.forEach((sensorData, index) => {
          const sampleDate = dayjs(sensorData.date);
          if (
            sampleDate.isSameOrAfter(fromDateDayjs) &&
            sampleDate.isSameOrBefore(toDateDayjs)
          ) {
            if (lowerRange == null) {
              lowerRange = index;
            }
            upperRange = index;
          }
        });
        this.validateRange(currentSensorSample, lowerRange, upperRange);
      }
    }
  }

  resetSensorsRanges() {
    if (this.sensorData) {
      for (const sensorKey in this.sensorData) {
        let currentSensorSample = this.sensorData[sensorKey];
        this.validateRange(
          currentSensorSample,
          Number.NEGATIVE_INFINITY,
          Number.POSITIVE_INFINITY,
        );
      }
    }
  }

  updateChartAfterZoomChanges() {
    if (this.sensorData) {
      for (const sensorKey in this.sensorData) {
        let currentSensorSample = this.sensorData[sensorKey];
        const filteredData = currentSensorSample.samples.slice(
          currentSensorSample.lowerRange,
          currentSensorSample.upperRange + 1,
        );
        var [spacedData, dataMaxValue] = this.spacedData(filteredData);
        const dataset = this.current_chart.data.datasets.find(
          (dataset) => dataset.label === currentSensorSample.id,
        );
        dataset.data = spacedData;
        dataset.label_indexes = [];
      }

      if (dataMaxValue < this.props.irrigation_hin) {
        this.current_chart.options.scales.y.max =
          parseInt(this.props.irrigation_hin) + 50;
      }

      // In archive mode, update y-axis bounds based on visible data but preserve zoom bounds
      if (this.props.field_state === 'archive') {
        this.updateYAxisBounds();
      }

      // Keep the hard 0 floor after any zoom/pan recalculation.
      this.applyForcedZeroYAxisFloorIfNeeded();

      this.current_chart.stop();
      this.current_chart.update('none');
    }
  }

  updateChart(sensorsData, suggestedMinimum) {
    var that = this;
    if (sensorsData) {
      if (this.current_chart) {
        this.current_chart.data.datasets = [];
        // let index = 0;
        let overallMaxValue = 0;
        let time_unit = 'day';
        for (const sensorKey in sensorsData) {
          let currentSensorSample = sensorsData[sensorKey];
          let filteredData = currentSensorSample.samples.slice(
            currentSensorSample.lowerRange,
            currentSensorSample.upperRange + 1,
          );
          let sensorTimeUnit = this.calculateTimeUnit(filteredData);
          // Use the coarsest time unit across all sensors to avoid
          // Chart.js "too far apart" errors when one sensor spans a wider range
          if (
            TIME_UNIT_ORDER.indexOf(sensorTimeUnit) >
            TIME_UNIT_ORDER.indexOf(time_unit)
          ) {
            time_unit = sensorTimeUnit;
          }
          var [spacedData, dataMaxValue] = this.spacedData(filteredData); // spacing the data since we don't need an high resolutino of items
          if (dataMaxValue > overallMaxValue) {
            overallMaxValue = dataMaxValue;
          }
          var labelIndexes = spacedData
            ? spacedData.map((sample) => {
                return sample.index;
              })
            : [];

          const resolvedColor = this.resolveColor(currentSensorSample.color);
          let newDataset = {
            lineTension: 0,
            type: 'line',
            label: currentSensorSample.id,
            transmitterID: currentSensorSample.transmitterId,
            displayName: currentSensorSample.displayName,
            fill: 'false',
            label_indexes: labelIndexes,
            borderColor: resolvedColor,
            backgroundColor: 'rgba(0,0,0,0.0)',
            pointBackgroundColor: resolvedColor,
            pointRadius: 0,
            pointHitRadius: 8,
            borderWidth: 2,
            data: spacedData,
          };

          this.current_chart.data.datasets.push(newDataset);
        }

        if (this.props.irrigation_hin) {
          this.current_chart.options.plugins.annotation.annotations[
            'a-line-1'
          ].value = this.props.irrigation_hin;
          this.current_chart.options.plugins.annotation.annotations[
            'a-line-1'
          ].label.content = 'Hin: ' + this.props.irrigation_hin;
          this.current_chart.options.plugins.annotation.annotations[
            'a-line-1'
          ].borderColor = 'rgba(246, 107, 122, 0.3)';
          this.current_chart.options.plugins.annotation.annotations[
            'a-line-1'
          ].label.display = true;
        } else {
          this.current_chart.options.plugins.annotation.annotations[
            'a-line-1'
          ].value = 0;
          this.current_chart.options.plugins.annotation.annotations[
            'a-line-1'
          ].borderColor = 'transparent';
          this.current_chart.options.plugins.annotation.annotations[
            'a-line-1'
          ].label.display = false;
        }
        this.current_chart.options.scales.x.time.unit = time_unit;
        // Ensure stepSize is not set to avoid errors with large ranges
        if (this.current_chart.options.scales.x.time.stepSize) {
          delete this.current_chart.options.scales.x.time.stepSize;
        }
        this.current_chart.options.scales.x.ticks = {
          ...this.current_chart.options.scales.x.ticks,
          display: false,
          font: {
            weight: '600',
          },
          maxTicksLimit: 20,
          callback: (function () {
            // Closure: detect day changes with integer math, only call dayjs for label formatting
            var tzOffsetMs = 0;
            var prevDayNum = -1;
            var MS_PER_DAY = 86400000;
            var needsDayLabels = ['hour', 'minute', 'day', 'week'].includes(
              time_unit,
            );
            var tzInitialized = false;

            return function (value, index, values) {
              if (needsDayLabels && values.length > 0) {
                if (!tzInitialized && that.timezone) {
                  tzOffsetMs =
                    dayjs(values[0].value).tz(that.timezone).utcOffset() *
                    60000;
                  tzInitialized = true;
                }
                var tickMs = values[index].value;
                var currentDayNum = Math.floor(
                  (tickMs + tzOffsetMs) / MS_PER_DAY,
                );

                if (currentDayNum !== prevDayNum) {
                  prevDayNum = currentDayNum;
                  return dayjs(tickMs).tz(that.timezone).format('DD MMM');
                }
              }
              return value;
            };
          })(),
        };

        let etcIndex,
          irrPrecpIndex = undefined;
        if (suggestedMinimum != undefined) {
          this.current_chart.options.scales.y.suggestedMin = suggestedMinimum;
        }

        // Always ensure irrigation_hin is visible if it exists
        // Set Y-axis max to whichever is greater: data max or irrigation_hin (plus padding)
        if (this.props.irrigation_hin) {
          const hinValue = parseInt(this.props.irrigation_hin);
          const requiredMax = Math.max(overallMaxValue, hinValue) + 50;
          this.current_chart.options.scales.y.max = requiredMax;
        }
        for (let index in this.current_chart.config.data.datasets) {
          if (
            this.current_chart.config.data.datasets[index]?.label ===
            'Irrigation Precipitation'
          ) {
            this.current_chart.config.data.datasets[index].borderWidth = 3;
            irrPrecpIndex = index;
          } else if (
            this.current_chart.config.data.datasets[index]?.label ===
            this.props.t('accumulated_graph_title_baseline_recommendation')
          ) {
            this.current_chart.config.data.datasets[index].borderWidth = 3;
            etcIndex = index;
          } else if (that.props.shouldDrawTodayMark) {
            this.current_chart.config.data.datasets[index].borderWidth = 1;
          }
          if (
            this.current_chart.config.data.datasets[index]?.label ===
            'Future Planned irrigation'
          ) {
            this.current_chart.config.data.datasets[index].borderDash = [2, 3];
            // don't display the 'past' of the Future Planned line
            for (let sample in this.current_chart.config.data.datasets[index]
              .data) {
              if (
                this.current_chart.config.data.datasets[index].data[sample][
                  'y'
                ] == 0
              ) {
                this.current_chart.config.data.datasets[index].data[sample][
                  'y'
                ] = null;
              }
            }
          }
        }
        if (irrPrecpIndex && etcIndex) {
          this.current_chart.config.data.datasets[etcIndex].fill =
            irrPrecpIndex;
          this.current_chart.config.data.datasets[etcIndex].backgroundColor =
            'rgb(238,180,141,0.2)'; // == #EEB48D with 0.2 opacity
        }

        // Handle x-axis and y-axis based on field state (archive vs active mode)
        if (this.props.field_state === 'archive') {
          // In archive mode, set bounds based on data range
          // Always set bounds for new field data or when not zoomed
          if (!this.current_chart.isZoomedOrPanned()) {
            const { earliestDate, latestDate } = this.findDataDateRange();
            if (
              earliestDate &&
              latestDate &&
              earliestDate.isValid() &&
              latestDate.isValid() &&
              earliestDate.valueOf() > 0 &&
              latestDate.valueOf() > 0
            ) {
              this.current_chart.options.scales.x.min = earliestDate.toDate();
              this.current_chart.options.scales.x.max = latestDate.toDate();
            } else {
              // If dates are invalid, clear min/max to let Chart.js auto-calculate
              delete this.current_chart.options.scales.x.min;
              delete this.current_chart.options.scales.x.max;
            }
            // Update y-axis bounds for archive mode based on visible data
            this.updateYAxisBounds();
          }
        } else {
          // In normal/active mode, allow the graph to extend to today (default behavior)
          // Remove any previously set min/max to let Chart.js handle it automatically
          if (this.current_chart.options.scales.x.min !== undefined) {
            delete this.current_chart.options.scales.x.min;
          }
          if (this.current_chart.options.scales.x.max !== undefined) {
            delete this.current_chart.options.scales.x.max;
          }

          // Reset y-axis to default behavior for normal mode, but preserve irrigation_hin adjustment
          // If irrigation_hin exists, we already set y.max above - don't reset it
          if (this.props.irrigation_hin) {
            // Keep the y.max we set earlier for irrigation_hin visibility
            delete this.current_chart.options.scales.y.min;
            this.current_chart.options.scales.y.suggestedMin = -10;
            delete this.current_chart.options.scales.y.suggestedMax;
          } else {
            this.resetYAxisBounds();
            // Honor an explicitly provided suggestedMin (e.g. 0 for the
            // accumulated water-input / nitrogen / discharge graphs) instead of
            // the shared -10 default that resetYAxisBounds() restores. Graphs
            // that pass undefined (Soil Temperature) keep -10, and Water
            // Potential keeps its own negative suggestedMinWPValue.
            this.current_chart.options.scales.y.suggestedMin =
              resolveActiveModeSuggestedMin(suggestedMinimum);
          }
        }

        // Final guard: for forceZeroYAxisMin graphs pin a hard min:0 as the LAST
        // y-axis mutation on every path (active, archive, irrigation_hin), so
        // nothing above can leave a negative floor. No-op for other graphs.
        this.applyForcedZeroYAxisFloorIfNeeded();

        this.current_chart.update('none');
      }
    }
  }

  calculateTimeUnit(filtered_samples) {
    var time_unit = 'day';
    if (filtered_samples.length > 0) {
      var first_date = filtered_samples[0].date;
      var last_date = filtered_samples[filtered_samples.length - 1].date;

      if (!first_date || !last_date) {
        return 'day';
      }

      // Samples may carry .date as a dayjs object or as a raw timestamp/string
      // depending on the upstream loader — normalize before calling .diff().
      first_date = dayjs(first_date);
      last_date = dayjs(last_date);

      var hour_diff = Math.abs(last_date.diff(first_date, 'hour'));
      var day_diff = hour_diff / 24;
      var year_diff = day_diff / 365;

      if (hour_diff < 6) {
        time_unit = 'minute';
      } else if (hour_diff < 72) {
        time_unit = 'hour';
      } else if (hour_diff < 380) {
        time_unit = 'day';
      } else if (day_diff < 90) {
        time_unit = 'week';
      } else if (year_diff < 2) {
        time_unit = 'month';
      } else {
        time_unit = 'year';
      }
    }

    return time_unit;
  }

  spacedData(samples) {
    let shrinker = 1;
    if (this.props.shouldSpaceData) {
      shrinker =
        samples.length > 2000
          ? 6
          : samples.length > 1000
            ? 5
            : samples.length > 500
              ? 3
              : samples.length > 50
                ? 2
                : 1;
    }

    let maxValue = -Infinity;
    const data = [];
    for (let i = 0; i < samples.length; i++) {
      const val = samples[i].value;
      if (typeof val === 'number' && !isNaN(val) && val > maxValue)
        maxValue = val;
      if (i % shrinker !== 0) continue;
      const sample = samples[i];
      const dateValue =
        sample.date && typeof sample.date.toDate === 'function'
          ? sample.date.toDate()
          : sample.date;
      data.push({
        y: sample.value,
        x: dateValue,
        index: sample.index,
        dataToDisplay: sample.dataToDisplay,
      });
    }
    if (maxValue === -Infinity) maxValue = 0;
    return [data, maxValue];
  }

  xAxesChange(type) {
    const min = dayjs(this.current_chart.scales.x.min);
    const isExistMax =
      dayjs().valueOf() - dayjs(this.current_chart.scales.x.max).valueOf() >
      60 * 1000;

    if (type === 'back') {
      let currentMin = this.current_chart.scales.x.min;
      let currentMax = isExistMax
        ? this.current_chart.scales.x.max
        : this.current_chart?.config?.data?.datasets[0]?.data[0]?.x;

      // check if possible go back
      if (this.props.changeXAxes(dayjs(currentMin), dayjs(currentMax))) {
        if (isExistMax) {
          this.current_chart.options.scales.x.max = dayjs(
            this.current_chart.scales.x.max,
          )
            .subtract(7, 'd')
            .toDate();
        } else {
          this.current_chart.options.scales.x.max = dayjs(
            this.current_chart?.config?.data?.datasets[0]?.data[0]?.x,
          )
            .subtract(7, 'd')
            .toDate();
        }
        this.current_chart.options.scales.x.min = min.subtract(7, 'd').toDate();
        this.current_chart.update('none');
        this.props.scaleGraph(
          dayjs(this.current_chart.scales.x.min).unix(),
          dayjs(this.current_chart.scales.x.max).unix(),
        );
      }
    } else if (type === 'forward') {
      if (!isExistMax) {
        return;
      }

      let currentMax = dayjs(this.current_chart.scales.x.max);
      let dateAfterForwardClick = currentMax.add(7, 'd');

      // check if possible go forward
      if (dateAfterForwardClick.unix() < dayjs().unix()) {
        this.current_chart.options.scales.x.min = dayjs(
          this.current_chart.scales.x.min,
        )
          .add(7, 'd')
          .toDate();
        this.current_chart.options.scales.x.max = dayjs(
          this.current_chart.scales.x.max,
        )
          .add(7, 'd')
          .toDate();
        this.current_chart.update('none');
        this.props.scaleGraph(
          dayjs(this.current_chart.scales.x.min).unix(),
          dayjs(this.current_chart.scales.x.max).unix(),
        );
      }
    }
    let filteredDataSet =
      this.current_chart?.config?.data?.datasets[0]?.data?.filter((dataObj) => {
        if (
          dayjs(dataObj.x).unix() >=
            dayjs(this.current_chart.scales.x.min).unix() &&
          dayjs(dataObj.x).unix() <=
            dayjs(this.current_chart.scales.x.max).unix()
        ) {
          return dataObj;
        }
      });
    let maxYValue;
    if (filteredDataSet === undefined) {
      maxYValue = 0;
    } else {
      maxYValue = Math.max(
        ...filteredDataSet?.map((dataObj) => {
          return dataObj.y;
        }),
      );
    }

    if (parseInt(this.props.irrigation_hin) > maxYValue) {
      this.current_chart.options.scales.y.max =
        parseInt(this.props.irrigation_hin) + 50;
    } else {
      this.current_chart.options.scales.y.max = maxYValue + 50;
    }
    this.current_chart.update('none');
  }

  zoom(type) {
    if (type === 'in') {
      this.current_chart.zoom(1.2, 'linear');
    } else {
      let isNeedZoom = false;
      for (let currentSensorKey in this.sensorData) {
        let currentSensor = this.sensorData[currentSensorKey];
        const dataset = this.current_chart.data.datasets.find(
          (dataset) => dataset.label === currentSensor.id,
        );

        // Compare dates properly - convert Date objects back to dayjs for comparison
        const lastSampleDate = dayjs(
          currentSensor.samples[currentSensor.samples.length - 2].date,
        );
        const firstSampleDate = dayjs(currentSensor.samples[1].date);
        const lastDatasetDate = dayjs(dataset.data[dataset.data.length - 1].x);
        const firstDatasetDate = dayjs(dataset.data[0].x);

        if (
          lastSampleDate.isBefore(lastDatasetDate) ||
          firstSampleDate.isAfter(firstDatasetDate)
        ) {
          isNeedZoom = true;
        }
      }
      if (isNeedZoom) {
        this.current_chart.zoom(0.8, 'linear');
      }
    }

    for (let currentSensorKey in this.sensorData) {
      let currentSensor = this.sensorData[currentSensorKey];

      let upper_range_zoom_factor = type == 'in' ? -1 : 1;
      let lower_range_zoom_factor = type == 'out' ? -1 : 1;

      let current_upper_range = currentSensor.upperRange;
      let current_lower_range = currentSensor.lowerRange;

      var index_diff = current_upper_range - current_lower_range;
      var zoom_value = parseInt(Math.max(index_diff / 10, 1));

      current_upper_range =
        current_upper_range + upper_range_zoom_factor * zoom_value;
      current_lower_range =
        current_lower_range + lower_range_zoom_factor * zoom_value;

      this.validateRange(
        currentSensor,
        current_lower_range,
        current_upper_range,
      );
    }

    clearTimeout(timer);
    timer = setTimeout(() => {
      this.updateChartAfterZoomChanges();
    }, 500);
  }

  // Reset graph to original zoom levels
  resetGraph() {
    this.resetSensorsRanges();
    this.updateChartAfterZoomChanges();
    this.current_chart.resetZoom();
  }

  render() {
    if (this.current_chart) {
      this.current_chart.config.options.scales.y.title.text = this.props.units;
      this.current_chart.config.options.plugins.filler = { propagate: false };
    }

    const pStyle = {
      pointerEvents: 'none',
    };

    let homeButton = (
      <a className='btn btn-link  pr-0 pl-0' onClick={() => this.resetGraph()}>
        <img
          className='image-buttons'
          src={require('images/icons/dashboard_states/zoom_extent_idle.svg')}
          onMouseOver={(obj) =>
            (obj.target.src = require('images/icons/dashboard_states/zoom_extent_hover.svg'))
          }
          onMouseDown={(obj) =>
            (obj.target.src = require('images/icons/dashboard_states/zoom_extent_press.svg'))
          }
          onMouseOut={(obj) =>
            (obj.target.src = require('images/icons/dashboard_states/zoom_extent_idle.svg'))
          }
          alt='home'
        />
      </a>
    );

    let secondButton = (
      <a
        className='btn btn-link  pr-0 pl-0'
        onClick={
          !this.props.shouldDisplayZoom
            ? () => this.xAxesChange('back')
            : () => this.zoom('in')
        }
      >
        <img
          className='image-buttons'
          src={
            !this.props.shouldDisplayZoom
              ? LeftArrow
              : require('images/icons/dashboard_states/zoom_in_idle.svg')
          }
          onMouseOver={
            this.props.shouldDisplayZoom
              ? (obj) =>
                  (obj.target.src = require('images/icons/dashboard_states/zoom_in_hover.svg'))
              : null
          }
          onMouseDown={
            this.props.shouldDisplayZoom
              ? (obj) =>
                  (obj.target.src = require('images/icons/dashboard_states/zoom_in_press.svg'))
              : null
          }
          onMouseOut={
            this.props.shouldDisplayZoom
              ? (obj) =>
                  (obj.target.src = require('images/icons/dashboard_states/zoom_in_idle.svg'))
              : null
          }
          alt='zoom in'
        />
      </a>
    );
    let thirdButton = (
      <a
        className='btn btn-link  pr-0 pl-0'
        onClick={
          !this.props.shouldDisplayZoom
            ? () => this.xAxesChange('forward')
            : () => this.zoom('out')
        }
      >
        <img
          className='image-buttons'
          src={
            !this.props.shouldDisplayZoom
              ? RightArrow
              : require('images/icons/dashboard_states/zoom_out_idle.svg')
          }
          onMouseOver={
            this.props.shouldDisplayZoom
              ? (obj) =>
                  (obj.target.src = require('images/icons/dashboard_states/zoom_out_hover.svg'))
              : null
          }
          onMouseDown={
            this.props.shouldDisplayZoom
              ? (obj) =>
                  (obj.target.src = require('images/icons/dashboard_states/zoom_out_press.svg'))
              : null
          }
          onMouseOut={
            this.props.shouldDisplayZoom
              ? (obj) =>
                  (obj.target.src = require('images/icons/dashboard_states/zoom_out_idle.svg'))
              : null
          }
          alt='zoom out'
        />
      </a>
    );

    return (
      <div className='d-flex flex-row h-100'>
        <div className='flex-fill'>
          <div
            ref={this._chartWrapperRef}
            className=''
            style={{
              position: 'relative',
              width: '99%',
              height: '100%',
              margin: 'auto',
            }}
          >
            <canvas id='overlay' className='position-absolute' style={pStyle} />
            <div className='' id='additionals'></div>
            <canvas className='' id='chartJSContainer'></canvas>
          </div>
        </div>
        {!this.props.hideNavigationButtons && (
          <div className='d-none d-sm-block' style={{ width: '36px' }}>
            <div
              className='d-flex justify-content-start align-items-end flex-column'
              style={{
                paddingTop: '8px',
                paddingRight: '4px',
                paddingLeft: 0,
              }}
            >
              {this.props.shouldDrawResetButton && homeButton}
              {secondButton}
              {thirdButton}
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = function (state) {
  const field = state.distribution_data.selected_entities.field;
  const role = state.distribution_data.selected_entities?.farming_unit?.role;

  return {
    plan_irr: field && field.historical_data && field.historical_data.plan_irr,
    fert: field && field.historical_data && field.historical_data.fert,
    hasPermission: ['moderator', 'admin'].includes(role),
  };
};

const mapDispatchToProps = {
  getFieldHistory: distributionActions.get_field_history,
};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(SensorGraph));
